import React, { useEffect, useRef, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Plus, Minus, RefreshCw } from "lucide-react";
import { fmtAmount } from "@/lib/stayflowApi";
import { MapAnchoredPopover } from "./MapAnchoredPopover";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MAP_STYLE = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "water", stylers: [{ color: "#cfe6f2" }] },
];

// Grabs the map instance + fits bounds once per id-set (only when autoFit is on).
const MapBrain = ({ geo, userLoc, autoFit, onMap }) => {
  const map = useMap();
  const lastKey = useRef("");
  useEffect(() => { if (map) onMap(map); }, [map, onMap]);
  useEffect(() => {
    if (!map || !window.google || geo.length === 0 || !autoFit) return;
    const key = geo.map((p) => p.id).join(",");
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (geo.length === 1 && !userLoc) { map.setCenter({ lat: geo[0].lat, lng: geo[0].lng }); map.setZoom(13); return; }
    const b = new window.google.maps.LatLngBounds();
    geo.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
    if (userLoc?.lat != null) b.extend({ lat: userLoc.lat, lng: userLoc.lng });
    map.fitBounds(b, 80);
  }, [map, geo, userLoc, autoFit]);
  return null;
};

export const SplitMap = ({
  points = [], selectedId, hoveredId, onSelect, onHover, userLoc = null, focus = null,
  metaById = {}, searchAsMove, setSearchAsMove, onBoundsChange, onViewStay,
}) => {
  const geo = points.filter((p) => p.lat != null && p.lng != null);
  const [mapObj, setMapObj] = useState(null);
  const samRef = useRef(searchAsMove);
  samRef.current = searchAsMove;

  useEffect(() => {
    if (!mapObj || !window.google) return;
    const l = mapObj.addListener("idle", () => {
      if (!samRef.current) return;
      const b = mapObj.getBounds();
      if (!b) return;
      const ne = b.getNorthEast(), sw = b.getSouthWest();
      onBoundsChange({ north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() });
    });
    return () => l.remove();
  }, [mapObj, onBoundsChange]);

  if (!KEY) {
    return <div className="w-full h-full bg-paper-muted flex items-center justify-center text-ink-soft text-sm" data-testid="split-map-nokey">Map isn't configured yet.</div>;
  }

  const center = focus?.lat != null ? { lat: focus.lat, lng: focus.lng } : userLoc?.lat != null ? { lat: userLoc.lat, lng: userLoc.lng } : geo[0] ? { lat: geo[0].lat, lng: geo[0].lng } : { lat: 26.2, lng: 92.9 };
  const sel = selectedId ? geo.find((p) => p.id === selectedId) : null;
  const meta = sel ? metaById[sel.id] : null;
  const zoom = (d) => { if (mapObj) mapObj.setZoom((mapObj.getZoom() || 11) + d); };

  return (
    <div className="w-full h-full relative" data-testid="split-map">
      <APIProvider apiKey={KEY}>
        <Map
          mapId="DEMO_MAP_ID"
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={geo.length > 1 ? 11 : 13}
          gestureHandling="greedy"
          disableDefaultUI
          clickableIcons={false}
          styles={MAP_STYLE}
        >
          <MapBrain geo={geo} userLoc={userLoc || focus} autoFit={!searchAsMove} onMap={setMapObj} />
          {geo.map((p) => {
            const active = p.id === selectedId || p.id === hoveredId;
            return (
              <AdvancedMarker key={p.id} position={{ lat: p.lat, lng: p.lng }} zIndex={active ? 30 : 1} onClick={() => onSelect(p.id)}>
                <div
                  onMouseEnter={() => onHover(p.id)}
                  onMouseLeave={() => onHover(null)}
                  data-testid={`split-marker-${p.id}`}
                  className={`rounded-full px-3 py-1.5 text-[13px] font-heading font-extrabold shadow-md border cursor-pointer transition-transform duration-200 ${
                    active ? "bg-ink text-white border-ink scale-125 shadow-xl ring-4 ring-ink/15 z-30" : "bg-white text-ink border-black/20 hover:scale-110"
                  }`}
                >
                  {fmtAmount(p.min_price_incl_gst ?? p.min_price_per_night) || "View"}
                </div>
              </AdvancedMarker>
            );
          })}

          {/* Selected-stay popover is rendered as a normal DOM overlay below the map
              (AdvancedMarker's <gmp-advanced-marker> swallows button clicks). */}

          {focus?.lat != null && (
            <AdvancedMarker position={{ lat: focus.lat, lng: focus.lng }} zIndex={6}>
              <div className="flex flex-col items-center" data-testid="map-focus-marker">
                <span className="rounded-full bg-ink text-white text-[11px] font-semibold px-2.5 py-1 shadow-md whitespace-nowrap">{focus.label}</span>
                <span className="w-3 h-3 -mt-0.5 rotate-45 bg-ink" />
              </div>
            </AdvancedMarker>
          )}
          {userLoc?.lat != null && (
            <AdvancedMarker position={{ lat: userLoc.lat, lng: userLoc.lng }} zIndex={5}>
              <div className="w-4 h-4 rounded-full bg-blue-600 border-2 border-white shadow-md" />
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>

      {/* Search-as-I-move toggle */}
      <button
        onClick={() => setSearchAsMove((v) => !v)}
        data-testid="split-search-as-move"
        aria-pressed={searchAsMove}
        className={`absolute top-4 left-1/2 -translate-x-1/2 z-20 rounded-full shadow-lg border px-4 py-2 flex items-center gap-2 text-xs font-semibold transition-colors ${
          searchAsMove ? "bg-ink text-white border-ink" : "bg-white text-ink-soft border-black/10"
        }`}
      >
        <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${searchAsMove ? "bg-white border-white" : "border-black/30"}`}>
          {searchAsMove && <span className="w-1.5 h-1.5 rounded-[1px] bg-ink" />}
        </span>
        <RefreshCw className="w-3.5 h-3.5" /> Search this area as I move
      </button>

      {/* Zoom controls */}
      <div className="absolute bottom-6 right-4 z-20 flex flex-col rounded-xl overflow-hidden shadow-lg border border-black/10 bg-white">
        <button onClick={() => zoom(1)} data-testid="split-zoom-in" className="w-10 h-10 flex items-center justify-center hover:bg-paper transition-colors border-b border-black/10"><Plus className="w-4 h-4 text-ink" /></button>
        <button onClick={() => zoom(-1)} data-testid="split-zoom-out" className="w-10 h-10 flex items-center justify-center hover:bg-paper transition-colors"><Minus className="w-4 h-4 text-ink" /></button>
      </div>

      {/* Selected-stay popover — glued next to the selected pin */}
      <MapAnchoredPopover
        map={mapObj}
        point={sel}
        meta={sel && meta ? { ...meta, id: sel.id } : null}
        onClose={() => onSelect(null)}
        onView={() => sel && onViewStay(sel.id)}
      />
    </div>
  );
};

export default SplitMap;
